// src/pages/admin/GstReportPage.jsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminReportsApi } from '@/api/services'
import { FileText, Download } from 'lucide-react'

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`

export default function GstReportPage() {
  const now = new Date()
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`)

  const { data, isLoading } = useQuery({
    queryKey: ['gst-report', month],
    queryFn: () => adminReportsApi.gst({ month }).then(r => r.data),
  })

  const report = data?.data
  const totals = report?.totals || {}
  const b2b = report?.b2b || []
  const b2c = report?.b2c || []
  const hsn = report?.hsn || []

  // Generate month options for last 12 months
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) }
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-pink-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">GST Filing Report</h1>
            <p className="text-xs text-gray-500">GSTR-1 summary for the selected month</p>
          </div>
        </div>
        <select value={month} onChange={e => setMonth(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm">
          {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { label: 'Total Invoices', value: totals.total_invoices || 0 },
              { label: 'Taxable Value', value: fmt(totals.total_taxable) },
              { label: 'CGST', value: fmt(totals.total_cgst) },
              { label: 'SGST', value: fmt(totals.total_sgst) },
              { label: 'IGST', value: fmt(totals.total_igst) },
            ].map(c => (
              <div key={c.label} className="bg-white rounded-xl border p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{c.label}</p>
                <p className="text-lg font-bold text-gray-900">{c.value}</p>
              </div>
            ))}
          </div>

          {/* B2B Invoices */}
          <div className="bg-white rounded-xl border">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="font-bold text-sm text-gray-700">B2B Invoices (by GSTIN)</h2>
              <span className="text-xs text-gray-500">{b2b.length} entries</span>
            </div>
            {b2b.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No B2B invoices this month</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-medium">GSTIN</th>
                      <th className="px-4 py-3 font-medium text-center">Invoices</th>
                      <th className="px-4 py-3 font-medium text-right">Taxable Value</th>
                      <th className="px-4 py-3 font-medium text-right">CGST</th>
                      <th className="px-4 py-3 font-medium text-right">SGST</th>
                      <th className="px-4 py-3 font-medium text-right">IGST</th>
                      <th className="px-4 py-3 font-medium text-right">Total Tax</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {b2b.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs">{row.buyer_gstin}</td>
                        <td className="px-4 py-3 text-center">{row.invoices}</td>
                        <td className="px-4 py-3 text-right">{fmt(row.taxable_value)}</td>
                        <td className="px-4 py-3 text-right">{fmt(row.cgst)}</td>
                        <td className="px-4 py-3 text-right">{fmt(row.sgst)}</td>
                        <td className="px-4 py-3 text-right">{fmt(row.igst)}</td>
                        <td className="px-4 py-3 text-right font-bold">{fmt(row.total_tax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* B2C Summary */}
          <div className="bg-white rounded-xl border">
            <div className="px-5 py-3 border-b">
              <h2 className="font-bold text-sm text-gray-700">B2C Summary (by State)</h2>
            </div>
            {b2c.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No B2C invoices this month</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-medium">State</th>
                      <th className="px-4 py-3 font-medium text-center">Invoices</th>
                      <th className="px-4 py-3 font-medium text-right">Taxable Value</th>
                      <th className="px-4 py-3 font-medium text-right">Total Tax</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {b2c.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 capitalize">{row.state}</td>
                        <td className="px-4 py-3 text-center">{row.invoices}</td>
                        <td className="px-4 py-3 text-right">{fmt(row.taxable_value)}</td>
                        <td className="px-4 py-3 text-right font-bold">{fmt(row.total_tax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* HSN Summary */}
          <div className="bg-white rounded-xl border">
            <div className="px-5 py-3 border-b">
              <h2 className="font-bold text-sm text-gray-700">HSN-wise Summary</h2>
            </div>
            {hsn.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No data</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-medium">HSN Code</th>
                      <th className="px-4 py-3 font-medium text-center">Invoices</th>
                      <th className="px-4 py-3 font-medium text-center">Quantity</th>
                      <th className="px-4 py-3 font-medium text-right">Taxable Value</th>
                      <th className="px-4 py-3 font-medium text-right">Total Tax</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {hsn.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono">{row.hsn_code}</td>
                        <td className="px-4 py-3 text-center">{row.invoices}</td>
                        <td className="px-4 py-3 text-center">{row.quantity}</td>
                        <td className="px-4 py-3 text-right">{fmt(row.taxable_value)}</td>
                        <td className="px-4 py-3 text-right font-bold">{fmt(row.total_tax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
